import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useRouter } from 'next/router'
import { paths } from '@/constants'
import {
  GetUserTorchApplicationQuery,
  GetUserTorchApplicationsQuery,
  TorchApplication,
} from '@/types/codegen-federation'
import {
  CREATE_TORCH_APPLICATION,
  GET_USER_TORCH_APPLICATION,
  GET_USER_TORCH_APPLICATIONS,
  UPDATE_TORCH_APPLICATION,
} from '../queries'

export enum TorchApplicationGenre {
  Action = 'Action',
  Adventure = 'Adventure',
  Comedy = 'Comedy',
  Suspense = 'Suspense',
  Children = 'Children',
  Romance = 'Romance',
  SciFi = 'SciFi',
  Documentary = 'Documentary',
  Drama = 'Drama',
  Reality = 'Reality',
  Food = 'Food',
  Musical = 'Musical',
  History = 'History',
  Fantasy = 'Fantasy',
  Standup = 'Standup',
  Improv = 'Improv',
}

export enum TorchApplicationProductionStage {
  PreProduction = 'PreProduction',
  InProgress = 'InProgress',
  PostProduction = 'PostProduction',
  Completed = 'Completed',
}

export type Torch = {
  id: string
  title: string
  poster: string
}

export enum TorchApplicationTypes {
  Concept = 'CONCEPT',
  CompletedEpisode = 'COMPLETED_EPISODE',
  CompletedMovie = 'COMPLETED_MOVIE',
  FullFilm = 'FULL_FILM',
}

export enum TorchApplicationStatus {
  Draft = 'DRAFT',
  PendingReview = 'PENDING_REVIEW',
  InReview = 'IN_REVIEW',
  Incomplete = 'INCOMPLETE',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
}

export enum TorchApplicationProductionType {
  Movie = 'Movie',
  Series = 'Series',
}

export enum TorchApplicationPresentationType {
  LiveAction = 'LiveAction',
  Animation = 'Animation',
  Animatic = 'Animatic',
}

export enum ResubmissionStatus {
  Pending = 'PENDING',
  Qualified = 'QUALIFIED',
  NotQualified = 'NOT_QUALIFIED',
  InProgress = 'IN_PROGRESS',
  Completed = 'COMPLETED',
}

export type TorchApplicationData = {
  isResubmission: boolean
  resubmissionStatus: ResubmissionStatus
  torchId?: string
  originalApplicationId?: string
  torchAcknowledgement?: boolean
  title?: string
  description?: string
  productionType?: TorchApplicationProductionType
  presentationType?: TorchApplicationPresentationType
  genres?: string[]
  ages?: string[]
  otherGenre?: string
  productionStage?: TorchApplicationProductionStage
  companyName?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  zipCode?: string
  country?: string
  copyrightName?: string
  filmmakerRole?: string
  filmmakerName?: string
  filmmakerEmail?: string
  filmmakerPhone?: string
  signatureDate?: string
  uploadAcknowledgements?: Array<'torch' | 'thumbnail' | 'movie'>
  paymentDate?: string
  source?: string
  projectId?: string
  allowShares?: boolean
}

type TorchApplicationContextType = {
  torchApplications: (TorchApplication | null)[]
  pageIndex: number
  pageStepIndex: number
  data: TorchApplicationData | undefined
  updateData: (data: Partial<TorchApplicationData>) => void
  setPage: (page: number) => void
  setPageStep: (step: number) => void
  setTorchApplication: (params: { id: string; step?: number; page?: number }) => void
  loading: boolean
  type?: TorchApplicationTypes | null
  updateType: (type: TorchApplicationTypes, torchId?: string) => void
  id?: string
  status?: TorchApplicationStatus | null
  isDraft: boolean
  updateStatus: (status: TorchApplicationStatus) => void
  refetchApplications: () => void
  applicationProjectId?: string | null
  setApplicationProjectId: (projectId: string | null | undefined) => void
}

const TorchApplicationContext = createContext<TorchApplicationContextType | null>(null)

export const useTorchApplication = () => {
  const context = useContext(TorchApplicationContext) as TorchApplicationContextType

  return context
}

type Props = PropsWithChildren

export const TorchApplicationProvider = ({ children }: Props) => {
  const { query, push, isReady, replace } = useRouter()

  const [type, setType] = useState<TorchApplicationTypes | null | undefined>()
  const [status, setStatus] = useState<TorchApplicationStatus | null | undefined>(TorchApplicationStatus.Draft)
  const [data, setData] = useState<TorchApplicationData>()
  const [applicationProjectId, setApplicationProjectId] = useState<string | null | undefined>()

  const {
    data: applications,
    loading,
    refetch: refetchApplications,
  } = useQuery<GetUserTorchApplicationsQuery>(GET_USER_TORCH_APPLICATIONS)
  const [createApplication] = useMutation(CREATE_TORCH_APPLICATION, {
    onCompleted: (data) => {
      if (!data.createTorchApplication) {
        replace(paths.guild.create.torch)
        // handle error
        return
      }

      const id = data.createTorchApplication.id
      shallowPush({ id, page: '2' })
      setData((prev) => ({ ...prev, id } as TorchApplicationData))
    },
    errorPolicy: 'all',
  })
  const [updateApplication] = useMutation(UPDATE_TORCH_APPLICATION, {
    errorPolicy: 'all',
  })
  const [loadApplication, { loading: loadingApplication }] = useLazyQuery<GetUserTorchApplicationQuery>(
    GET_USER_TORCH_APPLICATION,
    {
      onCompleted(data) {
        if (!data.torchApplication?.data) {
          // redirect to root if the application doesn't exist
          replace(paths.guild.create.torch)
          return
        }

        const nextData = data.torchApplication.data as TorchApplicationData

        setData(nextData)
        setType(data.torchApplication.type as TorchApplicationTypes)
        setStatus(data.torchApplication.status as TorchApplicationStatus)
        setApplicationProjectId(data.torchApplication?.projectId)
      },
      errorPolicy: 'all',
    },
  )

  useEffect(() => {
    if (query?.id) {
      loadApplication({ variables: { id: query.id } })
    } else {
      setData(undefined)
      setType(undefined)
    }
  }, [loadApplication, query?.id])

  const [pageIndex, pageStepIndex] = useMemo(() => {
    const pageIndex = Number(query?.page || 1) - 1
    const pageStepIndex = Number(query?.step || 0)

    return [pageIndex, pageStepIndex]
  }, [query])

  const updateData = useCallback(
    (updatedData: Partial<TorchApplicationData>) => {
      // resubmitting a torch
      if (!query?.id && updatedData?.originalApplicationId) {
        const originalApplicationId = updatedData?.originalApplicationId
        createApplication({
          variables: { input: { type: TorchApplicationTypes.Concept, data: { originalApplicationId } } },
        })
        return
      }

      if (!query?.id) {
        return
      }

      setData((prev) => {
        if (
          prev &&
          Object.keys(updatedData).every(
            (key) => prev[key as keyof TorchApplicationData] === updatedData[key as keyof TorchApplicationData],
          )
        ) {
          return prev
        }

        updateApplication({ variables: { input: { id: query.id, data: updatedData } } })
        return { ...prev, ...updatedData } as TorchApplicationData
      })
    },
    [updateApplication, createApplication, query.id],
  )

  const updateType = useCallback(
    (type: TorchApplicationTypes, originalApplicationId?: string) => {
      if (!query?.id) {
        createApplication({ variables: { input: { type, data: { originalApplicationId } } } })
        return
      }

      updateApplication({ variables: { input: { id: query.id, type } } })
      setType(type)
      setStatus(TorchApplicationStatus.Draft)
    },
    [query, createApplication, updateApplication],
  )

  const updateStatus = useCallback(
    (status: TorchApplicationStatus) => {
      if (!query?.id) {
        return
      }

      updateApplication({ variables: { input: { id: query.id, status } } })
      setStatus(status)
    },
    [query, updateApplication],
  )

  const shallowPush = useCallback(
    (queryToUpdate: { page?: string; step?: string; id?: string }) => {
      const nextQuery = { ...(query as Record<string, string>), ...queryToUpdate }
      if (queryToUpdate.step) {
        delete nextQuery.page

        if (queryToUpdate.step === '0') {
          delete nextQuery.id
        }
      }

      // remove step when changing pages or step is 0
      if (queryToUpdate.page || queryToUpdate.step === '0') {
        delete nextQuery.step
      }

      // remove page when it's the first page
      if (queryToUpdate.page === '1') {
        delete nextQuery.page
      }

      if (queryToUpdate.id) {
        nextQuery.id = queryToUpdate.id
      }

      const queryString = new URLSearchParams(nextQuery)
      queryString.delete('locale')
      push(
        { pathname: paths.guild.create.torch, query: nextQuery },
        `${paths.guild.create.torch}?${queryString.toString()}`,
        {
          shallow: true,
        },
      )
    },
    [push, query],
  )

  const value: TorchApplicationContextType = useMemo(
    () => ({
      torchApplications: applications?.torchApplications || [],
      data,
      loading: loading || loadingApplication,
      pageIndex,
      pageStepIndex,
      updateData,
      applicationProjectId,
      setApplicationProjectId: (projectId: string | null | undefined) => setApplicationProjectId(projectId),
      setPage: (page) => shallowPush({ page: String(page) }),
      setPageStep: (stepIndex) => shallowPush({ step: String(stepIndex) }),
      setTorchApplication: ({ id, step, page }) =>
        shallowPush({ id, step: String(step || ''), page: String(page || '') }),
      id: query?.id ? String(query.id) : undefined,
      type,
      updateType,
      status,
      isDraft: status === TorchApplicationStatus.Draft,
      updateStatus,
      refetchApplications,
    }),
    [
      applications?.torchApplications,
      data,
      loading,
      loadingApplication,
      pageIndex,
      pageStepIndex,
      updateData,
      applicationProjectId,
      query.id,
      type,
      updateType,
      status,
      updateStatus,
      refetchApplications,
      shallowPush,
    ],
  )

  return (
    <TorchApplicationContext.Provider value={value}>{isReady && !loading && children}</TorchApplicationContext.Provider>
  )
}
